import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDayLock } from "../hooks/useDayLock";
import { useBuilderQuery } from "../hooks/useBuilderQuery";
import { ledgerApi, salesApi, unwrap, unwrapList } from "../api/resources";
import { useToast } from "../context/ToastContext";
import { Button, Card, Empty, Field, Input, Loader, Modal } from "../components/ui";
import { VoucherChooser } from "../components/VoucherChooser";
import { entryLabel, formatMoney, todayLabel } from "../utils/format";
import { paymentLabel } from "../utils/finance";

export default function Dashboard() {
  const { isAdmin, isBuilder, query, ready } = useBuilderQuery();
  const toast = useToast();
  const navigate = useNavigate();
  const { reload: reloadDays } = useDayLock();
  const [ledger, setLedger] = useState(null);
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [opening, setOpening] = useState("");
  const [busy, setBusy] = useState(false);
  const [closeOpen, setCloseOpen] = useState(false);
  const [voucherOpen, setVoucherOpen] = useState(false);

  const load = async () => {
    if (!ready) {
      setLedger(null);
      setSales([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [ledgerRes, salesRes] = await Promise.all([
        ledgerApi.today(query),
        salesApi.list({ ...query, limit: 6, status: "active" }),
      ]);
      setLedger(unwrap(ledgerRes));
      setSales(unwrapList(salesRes).items);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.builderId, isAdmin]);

  const setOpeningBalance = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      await ledgerApi.setOpening({ openingBalance: Number(opening) });
      toast.success("Opening cash saved");
      setOpening("");
      load();
      reloadDays();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setBusy(false);
    }
  };

  const endDay = async () => {
    setBusy(true);
    try {
      await ledgerApi.endDay();
      toast.success("Today is closed");
      setCloseOpen(false);
      load();
      reloadDays();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <Loader />;

  if (!ready) {
    return (
      <Empty
        title="Add a builder first"
        text="Choose a builder at the top, then you will see their cashbook."
        action={
          <Button onClick={() => navigate("/builders")} icon="plus">
            Add builder
          </Button>
        }
      />
    );
  }

  if (ledger?.needsOpeningBalance) {
    return (
      <div className="stack">
        <header className="page-head">
          <div>
            <p className="eyebrow">{todayLabel()}</p>
            <h1>Start your cashbook</h1>
          </div>
        </header>
        <Card className="welcome-card">
          <h2>How much cash do you have today?</h2>
          <p>
            Write this only once. After that, every sale, installment and expense will add itself to
            the book.
          </p>
          {isBuilder ? (
            <form className="opening-form" onSubmit={setOpeningBalance}>
              <Field label="Opening cash (Rs)">
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  required
                  value={opening}
                  onChange={(event) => setOpening(event.target.value)}
                  placeholder="Example: 500000"
                />
              </Field>
              <Button type="submit" disabled={busy}>
                Open cashbook
              </Button>
            </form>
          ) : (
            <p className="muted">This builder has not set opening cash yet.</p>
          )}
        </Card>
      </div>
    );
  }

  return (
    <div className="stack">
      <header className="page-head">
        <div>
          <p className="eyebrow">{todayLabel()}</p>
          <h1>Today's cash</h1>
        </div>
        <div className="head-actions">
          {isBuilder ? (
            <Button icon="plus" onClick={() => setVoucherOpen(true)}>
              Add voucher
            </Button>
          ) : null}
          {isBuilder && ledger?.status === "open" ? (
            <Button variant="warn" onClick={() => setCloseOpen(true)}>
              Close today
            </Button>
          ) : null}
        </div>
      </header>

      {ledger?.status === "closed" ? (
        <div className="banner">Today's book is closed. New entries will go in tomorrow.</div>
      ) : null}

      <div className="stat-grid">
        <article className="stat">
          <span>Morning cash</span>
          <strong>{formatMoney(ledger?.openingBalance)}</strong>
        </article>
        <article className="stat stat-in">
          <span>Money in today</span>
          <strong>{formatMoney(ledger?.totalCredits)}</strong>
        </article>
        <article className="stat stat-out">
          <span>Spent today</span>
          <strong>{formatMoney(ledger?.totalDebits)}</strong>
        </article>
        <article className="stat stat-now">
          <span>Cash now</span>
          <strong>{formatMoney(ledger?.closingBalance)}</strong>
        </article>
      </div>

      {isBuilder ? (
        <div className="quick-grid compact-quick">
          <Link className="quick" to="/sales">
            <b>Collect installment</b>
            <span>Remaining will go down</span>
          </Link>
          <Link className="quick" to="/vendors/udhar">
            <b>Credits</b>
            <span>Vendors with udhar due</span>
          </Link>
        </div>
      ) : null}

      <Card>
        <div className="card-head">
          <h2>Customers still paying</h2>
          <Link to="/sales">See all</Link>
        </div>
        {sales.length === 0 ? (
          <Empty title="Nobody is pending" text="Add a new sale, or all installments are paid." />
        ) : (
          <div className="list">
            {sales.map((sale) => (
              <article key={sale.id} className="list-row">
                <Link to={`/sales/${sale.id}`}>
                  <b>{sale.buyerName}</b>
                  <span>
                    {sale.propertyName} · {sale.project?.name}
                  </span>
                </Link>
                <div className="list-money">
                  <small>Remaining</small>
                  <strong>{formatMoney(sale.remainingAmount)}</strong>
                </div>
                {isBuilder ? (
                  <Link className="btn btn-sm" to={`/sales?collect=${sale.id}`}>
                    Collect
                  </Link>
                ) : null}
              </article>
            ))}
          </div>
        )}
      </Card>

      <Card>
        <div className="card-head">
          <h2>Today's entries</h2>
        </div>
        {(ledger?.entries || []).length === 0 ? (
          <Empty title="No entries yet" text="A sale, installment or expense will show up here." />
        ) : (
          <div className="list">
            {ledger.entries.map((entry) => (
              <div key={entry.id} className="list-row">
                <div>
                  <b>{entry.description}</b>
                  <span>
                    {entryLabel(entry.type)}
                    {entry.paymentType ? ` · ${paymentLabel(entry.paymentType)}` : ""}
                    {entry.bankName || entry.bank?.name ? ` · ${entry.bankName || entry.bank.name}` : ""}
                  </span>
                </div>
                <strong className={entry.direction === "credit" ? "plus" : "minus"}>
                  {entry.direction === "credit" ? "+" : "−"} {formatMoney(entry.amount)}
                </strong>
              </div>
            ))}
          </div>
        )}
      </Card>

      <VoucherChooser open={voucherOpen} onClose={() => setVoucherOpen(false)} />

      <Modal
        open={closeOpen}
        title="Close today?"
        onClose={() => setCloseOpen(false)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setCloseOpen(false)}>
              No
            </Button>
            <Button variant="warn" disabled={busy} onClick={endDay}>
              Yes, close it
            </Button>
          </>
        }
      >
        <p>After closing, you cannot add a sale, installment or expense today. Tomorrow opens itself.</p>
      </Modal>
    </div>
  );
}
